/**
 * Preview Snapshot Script
 *
 * Injected into HTML responses from the preview subdomain.
 * Listens for postMessage({ type: 'papan:snapshot' }) from the parent frame,
 * then serializes the fully rendered DOM into a self-contained HTML string:
 *
 * - <canvas> elements → <img> with toDataURL()
 * - <img> elements → src replaced with data URIs
 * - <link> stylesheets → inlined as <style> tags
 * - <script> tags → stripped entirely
 *
 * The result is a static, script-free HTML snapshot that can be safely
 * rendered same-origin for html2canvas PDF export.
 *
 * ⚠️ MUST STAY IMPORT-FREE. Although this file lives under `api/`, it is
 * exposed via the client-safe `@papan/files/api/snapshot-script` subpath
 * and bundled into the WEB app (HtmlViewer injects it into the inline
 * srcDoc). It is safe only because it has ZERO imports — the whole module
 * body is a template string. There is no boundary-lint rule guarding this;
 * adding ANY import here (even `import type` from a sibling `api/` module)
 * would transitively pull server code — hono, @papan/database, node:crypto,
 * getStorage — into the client bundle and break/poison the web build. If
 * this ever needs a dependency, relocate it to a client-safe package first.
 */

/**
 * Returns the bridge script as a string to inject before </body>.
 * Handles: snapshot capture, scroll sync, and navigation detection.
 *
 * Used for BOTH the server-injected session preview (no opts) AND the
 * client-side inline-mode srcDoc (`opts.initialScrollTop` for scroll
 * restore). Sharing one builder keeps the snapshot serialization
 * (canvas/img/stylesheet inlining) identical across both modes so
 * `requestSnapshot()` — and therefore PDF export fidelity — works the
 * same whether the preview runs at the preview origin or as an inline
 * sandboxed srcDoc.
 */
/**
 * The agent eval bridge (preview_eval) — interpolated into the IIFE body only
 * when `getSnapshotScript` is called with `evalBridge: true`. References
 * `rememberOrigin` / `parentOrigin` / `evalEnabled` / `parent`, all defined in
 * the surrounding IIFE. Kept as a string constant (NOT an import — this module
 * must stay import-free; see the file header).
 */
const EVAL_BRIDGE_SECTION = `
  // ─── Agent eval bridge (app-inspect / preview_eval) ───────────────
  // "DevTools evaluate-script" for the agent: run a JS snippet in THIS live
  // preview and return a bounded, structured-clone-safe view of the result — so
  // the agent can ASK the running app ("did papan.db.run return rows?", "is that
  // node in the DOM after render?") instead of guessing.
  //
  // SECURITY — what actually contains this (it is NOT frame-ancestors; the serve
  // path sends NO frame-ancestors directive at all and drops X-Frame-Options, so
  // ANY ancestor can frame/postMessage in — which is exactly why the real
  // containment is): (1) this iframe runs under an OPAQUE-origin sandbox WITHOUT
  // allow-same-origin (PREVIEW_SANDBOX_DIRECTIVE, preview.ts — pinned by a test),
  // so evaluated code has no real origin, no cookies, no same-origin storage;
  // and (2) the papan.* SDK is brokered by the PARENT (HtmlViewer does the
  // authenticated fetch), so a hostile page that framed a known session URL and
  // posted its own enable-eval+eval would get only inert JS in an opaque box with
  // a DEAD SDK — no new authority. The runtime enable gate below (off until an
  // EDITABLE editor sends papan:enable-eval) is a UX / defense-in-depth layer,
  // NOT the security boundary; and this whole section is only INJECTED for
  // opted-in (editor / non-share) previews, so share-view carries no eval code at
  // all. eval here grants no authority the editor lacks — they can already edit
  // the app's own code. (evalEnabled is declared up by saveHotkeyOn; enable is
  // handled in the command listener above.)
  //
  // #1408: on an app PREVIEW origin (<label>.<APP_PREVIEW_DOMAIN>) there is no opaque
  // sandbox — eval runs with the app's own storage. That is the app's own data; the
  // containment there is R1 (no cookies) + R2 (no API authority on that host) + R4 (this
  // bridge only for editor sessions) + this script obeying only Papan (fromPapan: the
  // parent window, at Papan's exact origin). See docs/MINI_APP_SECURITY.md, App preview origins.

  var EVAL_MAX_STRING = 5000;    // per-string cap (chars)
  var EVAL_MAX_ARRAY = 200;      // per-array element cap
  var EVAL_MAX_KEYS = 200;       // per-object key / map-entry cap
  var EVAL_MAX_DEPTH = 8;        // recursion cap
  var EVAL_MAX_TOTAL = 200000;   // ~200KB hard ceiling on the serialized payload

  // Convert any runtime value into a bounded, structured-clone-safe descriptor
  // (primitives, plain arrays/objects, or tagged placeholders for DOM nodes,
  // functions, errors, typed arrays, Map/Set, cycles). NEVER throws (top-level
  // catch) and NEVER iterates unbounded (typed arrays / Map / Set are sampled via
  // length/iterator, never Object.keys — Object.keys(new Uint8Array(1e8)) would
  // materialize 100M index strings before any cap).
  function evalDescribe(v, depth, seen) {
    if (v === null) return null;
    var t = typeof v;
    if (t === 'string') return v.length > EVAL_MAX_STRING ? (v.slice(0, EVAL_MAX_STRING) + '…[+' + (v.length - EVAL_MAX_STRING) + ' chars]') : v;
    if (t === 'number' || t === 'boolean') return v;
    if (t === 'undefined') return '[undefined]';
    if (t === 'bigint') return v.toString() + 'n';
    if (t === 'symbol') return v.toString();
    if (t === 'function') return '[Function' + (v.name ? ' ' + v.name : '') + ']';
    if (typeof Node !== 'undefined' && v instanceof Node) {
      if (v.nodeType === 1) {
        var el = v;
        var tag = '<' + el.tagName.toLowerCase() + (el.id ? ' id="' + el.id + '"' : '') + (el.className && typeof el.className === 'string' ? ' class="' + el.className + '"' : '') + '>';
        var txt = (el.textContent || '').replace(/\\s+/g, ' ').trim();
        return { __node: tag, text: txt ? txt.slice(0, 200) : '', childCount: el.children ? el.children.length : 0 };
      }
      return '[Node ' + (v.nodeName || '?') + ']';
    }
    if (v instanceof Error) return { __error: v.name || 'Error', message: String(v.message || ''), stack: v.stack ? String(v.stack).slice(0, 1000) : '' };
    if (depth >= EVAL_MAX_DEPTH) return '[…max depth]';
    if (seen.indexOf(v) !== -1) return '[Circular]';
    seen.push(v);
    var out;
    try {
      if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(v) && typeof v.length === 'number') {
        // Typed array — sample by index (bounded), NEVER Object.keys.
        var ta = [];
        for (var ti = 0; ti < v.length && ti < EVAL_MAX_ARRAY; ti++) ta.push(v[ti]);
        out = { __typedArray: (v.constructor && v.constructor.name) || 'TypedArray', length: v.length, sample: ta };
        if (v.length > EVAL_MAX_ARRAY) out.truncated = true;
      } else if (typeof Map !== 'undefined' && v instanceof Map) {
        var me = []; var mit = v.entries(); var mstep;
        while (me.length < EVAL_MAX_KEYS && !(mstep = mit.next()).done) {
          me.push([evalDescribe(mstep.value[0], depth + 1, seen), evalDescribe(mstep.value[1], depth + 1, seen)]);
        }
        out = { __map: v.size, entries: me };
        if (v.size > EVAL_MAX_KEYS) out.truncated = true;
      } else if (typeof Set !== 'undefined' && v instanceof Set) {
        var sv = []; var sit = v.values(); var sstep;
        while (sv.length < EVAL_MAX_ARRAY && !(sstep = sit.next()).done) sv.push(evalDescribe(sstep.value, depth + 1, seen));
        out = { __set: v.size, values: sv };
        if (v.size > EVAL_MAX_ARRAY) out.truncated = true;
      } else if (Object.prototype.toString.call(v) === '[object Array]') {
        out = [];
        for (var i = 0; i < v.length && i < EVAL_MAX_ARRAY; i++) out.push(evalDescribe(v[i], depth + 1, seen));
        if (v.length > EVAL_MAX_ARRAY) out.push('…[+' + (v.length - EVAL_MAX_ARRAY) + ' more of ' + v.length + ']');
      } else {
        out = {};
        var keys;
        try { keys = Object.keys(v); } catch (ke) { keys = []; out.__unreadable = true; }
        for (var k = 0; k < keys.length && k < EVAL_MAX_KEYS; k++) {
          try { out[keys[k]] = evalDescribe(v[keys[k]], depth + 1, seen); }
          catch (ge) { out[keys[k]] = '[unreadable]'; }
        }
        if (keys.length > EVAL_MAX_KEYS) out.__truncated = '+' + (keys.length - EVAL_MAX_KEYS) + ' more keys';
      }
    } catch (de) {
      out = '[unreadable: ' + (de && de.message ? String(de.message).slice(0, 200) : 'error') + ']';
    } finally {
      seen.pop();
    }
    return out;
  }

  window.addEventListener('message', function(e) {
    if (!fromPapan(e)) return;
    if (!e.data || e.data.type !== 'papan:eval') return;
    rememberOrigin(e);
    var reqId = e.data.reqId || '';
    var replyTarget = (e.origin && e.origin !== 'null') ? e.origin : parentOrigin;
    function reply(payload) {
      payload.type = 'papan:eval:result';
      payload.reqId = reqId;
      // #385: hand back the console shim's per-load session id so the agent's
      // preview_eval can read errors captured for exactly this load.
      try { payload.consoleSess = (typeof window.__papanConsoleSess === 'string' ? window.__papanConsoleSess : ''); } catch (ce) {}
      try { parent.postMessage(payload, replyTarget); }
      catch (pe) { parent.postMessage({ type: 'papan:eval:result', reqId: reqId, ok: false, error: 'result was not serializable' }, replyTarget); }
    }
    if (!evalEnabled) {
      reply({ ok: false, error: 'The eval bridge is not enabled for this preview (open the app in the editable editor).' });
      return;
    }
    var code = typeof e.data.code === 'string' ? e.data.code : '';
    if (!code) { reply({ ok: false, error: 'No code provided.' }); return; }
    var started = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    Promise.resolve().then(function() {
      // Compile as an async function so await works. Try EXPRESSION mode first
      // (DevTools-like: a bare expression is returned), fall back to STATEMENT
      // mode (multi-statement bodies that return explicitly). Indirect eval
      // runs in the app's global scope — window, document, and papan.* are all
      // visible. Requires script-src 'unsafe-eval' (present on live previews).
      var runner;
      try { runner = (0, eval)('(async function(){ return (' + code + '\\n); })'); }
      catch (se) { runner = (0, eval)('(async function(){ ' + code + '\\n })'); }
      return runner();
    }).then(function(value) {
      var ms = started ? Math.round(performance.now() - started) : undefined;
      var desc = evalDescribe(value, 0, []);
      var size;
      try { size = JSON.stringify(desc).length; } catch (je) { desc = '[unserializable result]'; size = 0; }
      if (size > EVAL_MAX_TOTAL) {
        reply({ ok: true, truncated: true, valueType: (value === null ? 'null' : typeof value), value: '[result too large (' + size + ' bytes) — narrow your snippet to return less, e.g. counts or a slice]', ms: ms });
      } else {
        reply({ ok: true, valueType: (value === null ? 'null' : typeof value), value: desc, ms: ms });
      }
    }).catch(function(err) {
      var ms = started ? Math.round(performance.now() - started) : undefined;
      reply({ ok: false, error: (err && err.message) ? String(err.message) : String(err), stack: (err && err.stack) ? String(err.stack).slice(0, 1000) : '', ms: ms });
    });
  });
`

export function getSnapshotScript(opts?: { initialScrollTop?: number; evalBridge?: boolean; parentOrigin?: string }): string {
  const restore = opts?.initialScrollTop != null && opts.initialScrollTop > 0
    ? `
  (function restore(n){
    window.scrollTo(0, ${opts.initialScrollTop});
    if (n < 30 && window.scrollY < ${opts.initialScrollTop})
      setTimeout(function(){ restore(n + 1); }, 100);
  })(0);`
    : ''
  // The agent eval bridge (preview_eval) is injected ONLY when the caller opts in
  // via `evalBridge` — the editable author editor (HtmlViewer, gated on
  // onSaveHotkey) and the author's own session preview (preview-serve, non-share
  // sessions). Share-view / read-only / version previews never receive the eval
  // code AT ALL, so the runtime enable gate is a second layer, not the only one.
  const evalSection = opts?.evalBridge ? EVAL_BRIDGE_SECTION : ''
  return `<script data-papan-bridge>
(function(){
  // ─── Parent-origin targeting ──────────────────────────────────
  // This script runs in an opaque-origin sandbox; the embedder is the
  // Papan app on a real origin. We DON'T know that origin at serve time
  // (the proxy strips X-Forwarded-Host), so we learn it from the first
  // inbound message and target outbound posts at it instead of '*'. The
  // '*' fallback (before any inbound message) is safe because the
  // embedding app enforces frame-ancestors 'self' / X-Frame-Options, so
  // there is no hostile ancestor that could intercept these low-value
  // (URL / scroll) broadcasts. Replies to a request always target that
  // request's own origin — the canonical request/reply pattern.
  var parentOrigin = '*';
  function rememberOrigin(e) {
    if (e && e.origin && e.origin !== 'null') parentOrigin = e.origin;
  }
  // #1408: only Papan drives this bridge — the parent window, and on the app's own preview origin
  // (a real origin, no opaque sandbox around it) only Papan's exact origin. A frame the app embeds
  // or a popup can't enable eval, run it, or read a snapshot.
  var PAPAN_PARENT = ${JSON.stringify(opts?.parentOrigin ?? '')};
  if (PAPAN_PARENT) parentOrigin = PAPAN_PARENT;
  function fromPapan(e) {
    return !!e && e.source === parent && (!PAPAN_PARENT || e.origin === PAPAN_PARENT);
  }

  // ─── Navigation detection ─────────────────────────────────────
  // The inline srcDoc document, with or without a fragment: a hash router's
  // pushState lands on 'about:srcdoc#/…' (the storage shim resolves it on the
  // frame's own address), and that is still not a navigation target — as a
  // parent-side "Back" src it renders an EMPTY document.
  function isSrcdoc() { return location.href.split('#')[0] === 'about:srcdoc'; }

  // Report current URL to parent on each page load
  window.addEventListener('load', function() {
    var url = location.href;
    if (url && !isSrcdoc()) parent.postMessage({ type: 'papan:nav', url: url }, parentOrigin);
  });

  // ─── Scroll sync ──────────────────────────────────────────────
  // Report scroll position to parent (debounced)
  var scrollTimer;
  window.addEventListener('scroll', function() {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(function() {
      parent.postMessage({ type: 'papan:vs', st: window.scrollY, sl: window.scrollX }, parentOrigin);
    }, 100);
  });

  // ─── Save hotkey forwarding ───────────────────────────────────
  // A focused iframe swallows Cmd/Ctrl+S, so the app's save handler never
  // sees it. When the embedder is an editable editor it opts in via
  // papan:enable-save-hotkey; we then preventDefault (kill the browser's
  // "Save page" dialog) and forward the intent. Off by default so public
  // preview/share pages keep native behavior.
  var saveHotkeyOn = false;
  var evalEnabled = false; // agent eval bridge — opt-in, see the eval section below
  // Undo/redo is NOT handled here, deliberately. This listener is armed for every
  // editable editor, design mode on or off, so swallowing Cmd+Z here killed the
  // native undo of any <input>/<textarea>/contenteditable in the PREVIEWED page —
  // and the parent then dropped the forwarded message because design mode was
  // off, leaving nothing in its place. The forwarding lives in the design-mode
  // script instead, which knows whether design mode is live and whether an inline
  // edit is in progress.
  window.addEventListener('keydown', function(e) {
    if (!saveHotkeyOn) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      parent.postMessage({ type: 'papan:hotkey-save' }, parentOrigin);
    }
  });

  // Accept scroll-to and history navigation commands from parent
  window.addEventListener('message', function(e) {
    if (!fromPapan(e)) return;
    if (!e.data) return;
    rememberOrigin(e);
    if (e.data.type === 'papan:scrollTo') {
      window.scrollTo(e.data.sl || 0, e.data.st || 0);
    } else if (e.data.type === 'papan:nav:back') {
      history.back();
    } else if (e.data.type === 'papan:nav:forward') {
      history.forward();
    } else if (e.data.type === 'papan:enable-save-hotkey') {
      saveHotkeyOn = true;
    } else if (e.data.type === 'papan:enable-eval') {
      evalEnabled = true;
    } else if (e.data.type === 'papan:ping') {
      // Liveness beacon for the agent inspection tools (preview_eval /
      // preview_screenshot). A double-buffered preview holds TWO iframes; the
      // just-swapped-in one may have its src set but this bridge not yet loaded
      // (mid-reload), and the swapped-out spare may be a blank src="" frame with
      // no bridge at all. The client can't tell which buffer is answerable from
      // the DOM (visibility flips before load completes), so it pings every
      // candidate and evals only the one that pongs. Ungated on purpose — this
      // reports "the bridge is LOADED and listening", independent of whether
      // eval is enabled; we echo evalEnabled so the client need not probe with
      // a real (side-effecting) eval just to learn readiness.
      var pingReply = (e.origin && e.origin !== 'null') ? e.origin : parentOrigin;
      parent.postMessage({ type: 'papan:pong', reqId: e.data.reqId || '', evalEnabled: evalEnabled }, pingReply);
    }
  });

${evalSection}
  // ─── Snapshot capture ─────────────────────────────────────────
  window.addEventListener('message', function(e) {
    if (!fromPapan(e)) return;
    if (!e.data || e.data.type !== 'papan:snapshot') return;
    rememberOrigin(e);
    var reqId = e.data.reqId || '';
    // Reply to the requester's own origin (not '*').
    var replyTarget = (e.origin && e.origin !== 'null') ? e.origin : parentOrigin;

    snapshot().then(function(html) {
      // #385: consoleSess lets preview_screenshot report errors from this load.
      var cs = '';
      try { cs = (typeof window.__papanConsoleSess === 'string' ? window.__papanConsoleSess : ''); } catch (ce) {}
      parent.postMessage({ type: 'papan:snapshot:result', reqId: reqId, html: html, consoleSess: cs }, replyTarget);
    }).catch(function(err) {
      parent.postMessage({ type: 'papan:snapshot:result', reqId: reqId, error: err.message }, replyTarget);
    });
  });

  function snapshot() {
    // Work on a clone so we don't modify the live DOM
    var clone = document.documentElement.cloneNode(true);
    var original = document.documentElement;

    // The synchronous steps run first (canvas rasterization reads the live
    // canvases; form-state copies live control values). The two NETWORK-bound
    // steps — inlining images and external stylesheets — touch disjoint nodes
    // (<img> vs <link>), so run them CONCURRENTLY instead of one-after-another:
    // their fetch latencies now overlap, which is the bulk of "preparing" time
    // on an asset-heavy page.
    convertCanvases(clone, original);
    syncFormState(original, clone);
    // Which image URLs did the LIVE page actually manage to load? Built from
    // the original tree (by src, not index — convertCanvases already inserted
    // extra imgs into the clone). Inlining re-fetches through the HTTP cache,
    // so a loaded image is instant; an image the preview itself could never
    // load would hang the snapshot reply for the full fetch timeout and then
    // fail anyway. Skip those: the export honestly mirrors the rendered page.
    var imgLoadMap = buildImgLoadMap(original);
    return Promise.all([
      convertImages(clone, imgLoadMap),
      inlineStylesheets(clone),
    ]).then(function() {
        injectBaseHref(clone);
        stripDesignArtifacts(clone);
        stripScripts(clone);
        return '<!DOCTYPE html>' + clone.outerHTML;
      });
  }

  // ─── Strip design-mode editor chrome ──────────────────────────────
  // When Export is clicked while the editor is in Design mode, the live
  // preview carries the injected inspector's overlay (hover/selection
  // outlines, the tag/id/class W-by-H badge, resize handles) as real DOM —
  // a fixed, top-layer [data-papan-design-overlay] div — plus a
  // contenteditable attribute on the element being text-edited. None of
  // that should appear in the exported PDF/image. Remove the overlay (all
  // inspector UI lives inside it or is tagged data-papan-design-ui) and
  // clear editing attributes. Runs on the CLONE, so the live editor is
  // untouched and design mode stays active for the user.
  function stripDesignArtifacts(clone) {
    var chrome = clone.querySelectorAll('[data-papan-design-overlay], [data-papan-design-ui], [data-papan-design-handle]');
    for (var i = chrome.length - 1; i >= 0; i--) {
      if (chrome[i].parentNode) chrome[i].parentNode.removeChild(chrome[i]);
    }
    var edited = clone.querySelectorAll('[contenteditable]');
    for (var j = 0; j < edited.length; j++) edited[j].removeAttribute('contenteditable');
  }

  // ─── <base href> so relative URLs in inlined CSS still resolve ──
  // When inlineStylesheets() pulls CSS rules out of a same-origin
  // stylesheet, the rules' relative URLs (background-image: url(./x),
  // @font-face src, etc.) get serialized as-is. The PDF-capture iframe
  // doc.writes the snapshot on the MAIN APP origin with no path
  // context, so those relative URLs 404. A <base> pointing back at
  // the page's own URL fixes the resolution. (External absolute URLs
  // — CDN links etc. — are unaffected.)
  function injectBaseHref(clone) {
    var head = clone.querySelector('head');
    if (!head) return;
    if (head.querySelector('base')) return; // honor an existing base
    // Inline srcDoc has no resolvable base (about:srcdoc); the snapshot
    // inlines all resources as data URIs anyway, so skip.
    if (isSrcdoc()) return;
    var baseHref = location.href.replace(/[?#].*$/, '').replace(/[^/]+$/, '');
    var base = clone.ownerDocument.createElement('base');
    base.setAttribute('href', baseHref);
    head.insertBefore(base, head.firstChild);
  }

  // ─── Form state → attributes ──────────────────────────────────
  // cloneNode(true) copies the DOM TREE but not the live state of
  // form controls — input.value, checkbox.checked, and select.value
  // live as JS properties, not as the initial attributes the clone
  // ends up with. Walk both trees in lockstep and write current
  // state back into the clone as attributes so the serialized HTML
  // reflects what the user actually typed/picked.
  function syncFormState(original, clone) {
    var origCtrls = original.querySelectorAll('input, textarea, select');
    var cloneCtrls = clone.querySelectorAll('input, textarea, select');
    for (var i = 0; i < origCtrls.length; i++) {
      var orig = origCtrls[i];
      var cln = cloneCtrls[i];
      if (!cln) continue;
      var tag = orig.tagName;
      if (tag === 'INPUT') {
        var type = (orig.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') {
          if (orig.checked) cln.setAttribute('checked', '');
          else cln.removeAttribute('checked');
        } else if (type !== 'password' && type !== 'file') {
          // Skip password (avoid bleeding to PDF) and file (can't
          // serialize). Everything else round-trips via the value
          // attribute.
          cln.setAttribute('value', orig.value);
        }
      } else if (tag === 'TEXTAREA') {
        cln.textContent = orig.value;
      } else if (tag === 'SELECT') {
        var origOpts = orig.options;
        var clnOpts = cln.options;
        for (var j = 0; j < origOpts.length; j++) {
          if (!clnOpts[j]) continue;
          if (origOpts[j].selected) clnOpts[j].setAttribute('selected', '');
          else clnOpts[j].removeAttribute('selected');
        }
      }
    }
  }

  // ─── Canvas → <img> with data URI ───────────────────────────────
  function convertCanvases(clone, original) {
    var origCanvases = original.querySelectorAll('canvas');
    var cloneCanvases = clone.querySelectorAll('canvas');
    for (var i = 0; i < origCanvases.length; i++) {
      try {
        var dataUrl = origCanvases[i].toDataURL('image/png');
        var img = clone.ownerDocument.createElement('img');
        img.src = dataUrl;
        img.width = origCanvases[i].width;
        img.height = origCanvases[i].height;
        img.style.cssText = cloneCanvases[i].style.cssText || '';
        img.className = cloneCanvases[i].className || '';
        if (cloneCanvases[i].parentNode) {
          cloneCanvases[i].parentNode.replaceChild(img, cloneCanvases[i]);
        }
      } catch(e) { /* tainted canvas — leave as-is */ }
    }
  }

  // ─── Bounded asset fetching (B39) ───────────────────────────────
  // Pool of 6 concurrent fetches + 10s per-fetch timeout + 5MB per-asset
  // cap. Hostile/huge HTML used to Promise.all EVERY asset at once with
  // no deadline or size bound — hanging or OOMing the exporting tab.
  var SNAP_FETCH_LIMIT = 12;
  var SNAP_FETCH_TIMEOUT_MS = 10000;
  var SNAP_FETCH_MAX_BYTES = 5 * 1024 * 1024;
  var snapActive = 0;
  var snapQueue = [];
  function snapAcquire() {
    return new Promise(function(resolve) {
      if (snapActive < SNAP_FETCH_LIMIT) { snapActive++; resolve(); }
      else snapQueue.push(resolve);
    });
  }
  function snapRelease() {
    var next = snapQueue.shift();
    if (next) next();
    else snapActive--;
  }
  function limitedFetch(url) {
    return snapAcquire().then(function() {
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var t = setTimeout(function() { if (ctrl) ctrl.abort(); }, SNAP_FETCH_TIMEOUT_MS);
      return fetch(url, ctrl ? { signal: ctrl.signal } : {}).then(
        function(r) { clearTimeout(t); snapRelease(); return r; },
        function(e) { clearTimeout(t); snapRelease(); throw e; }
      );
    });
  }

  // ─── Images → data URI ──────────────────────────────────────────
  // Map of img src → whether the LIVE page loaded it (any instance). Keyed by
  // src because the clone may carry extra imgs (canvas conversions) that break
  // index-parallel walks.
  function buildImgLoadMap(original) {
    var m = {};
    var imgs = original.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (!im.src) continue;
      if (im.complete && im.naturalWidth > 0) m[im.src] = true;
      else if (!(im.src in m)) m[im.src] = false;
    }
    return m;
  }

  function convertImages(clone, loadMap) {
    var imgs = clone.querySelectorAll('img');
    var promises = [];
    for (var i = 0; i < imgs.length; i++) {
      (function(img) {
        // Skip already-inlined data URIs and SVGs
        if (!img.src || img.src.startsWith('data:')) return;
        // Skip URLs the live page could not load (broken/hanging) — the fetch
        // would stall the snapshot and fail the same way. Not-yet-loaded lazy
        // images also land here; the capture side eagerly re-loads them.
        if (loadMap[img.src] === false) return;
        promises.push(
          fetchAsDataUri(img.src)
            .then(function(uri) { img.src = uri; })
            .catch(function() { /* keep original src */ })
        );
      })(imgs[i]);
    }
    return Promise.all(promises);
  }

  function fetchAsDataUri(url) {
    return limitedFetch(url)
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        if (blob.size > SNAP_FETCH_MAX_BYTES) {
          throw new Error('asset too large to inline');
        }
        return new Promise(function(resolve, reject) {
          var reader = new FileReader();
          reader.onload = function() { resolve(reader.result); };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      });
  }

  // ─── Stylesheets → inline <style> ──────────────────────────────
  function inlineStylesheets(clone) {
    var links = clone.querySelectorAll('link[rel="stylesheet"]');
    var promises = [];
    for (var i = 0; i < links.length; i++) {
      (function(link) {
        // Try to read cssRules from the live document's matching stylesheet
        var cssText = getCssTextForHref(link.href);
        if (cssText) {
          var style = clone.ownerDocument.createElement('style');
          style.textContent = cssText;
          link.parentNode.replaceChild(style, link);
        } else if (link.href) {
          // Fallback: fetch the CSS file and inline it (bounded — B39)
          promises.push(
            limitedFetch(link.href)
              .then(function(r) { return r.text(); })
              .then(function(text) {
                if (text.length > SNAP_FETCH_MAX_BYTES) throw new Error('stylesheet too large to inline');
                var style = clone.ownerDocument.createElement('style');
                style.textContent = text;
                if (link.parentNode) link.parentNode.replaceChild(style, link);
              })
              .catch(function() {
                // Couldn't inline (CSP/CORS blocked the fetch). Keep
                // the link, but force crossorigin="anonymous" so when
                // the PDF-capture iframe loads it on the main app
                // origin, html2canvas can read its cssRules and apply
                // the styles. Most CDNs return CORS headers, so this
                // unlocks rendering for those cases. If the CDN
                // doesn't, the link still renders for the user via
                // computed styles — only html2canvas's deeper rule
                // introspection is degraded.
                link.setAttribute('crossorigin', 'anonymous');
              })
          );
        }
      })(links[i]);
    }
    return Promise.all(promises);
  }

  function getCssTextForHref(href) {
    try {
      var sheets = document.styleSheets;
      for (var i = 0; i < sheets.length; i++) {
        if (sheets[i].href === href) {
          var rules = sheets[i].cssRules;
          var text = '';
          for (var j = 0; j < rules.length; j++) text += rules[j].cssText + '\\n';
          return text;
        }
      }
    } catch(e) { /* cross-origin stylesheet — cssRules blocked */ }
    return null;
  }

  // ─── Strip all scripts ─────────────────────────────────────────
  function stripScripts(clone) {
    var scripts = clone.querySelectorAll('script');
    for (var i = scripts.length - 1; i >= 0; i--) {
      scripts[i].parentNode.removeChild(scripts[i]);
    }
    // Also strip inline event handlers
    var all = clone.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var attrs = all[i].attributes;
      for (var j = attrs.length - 1; j >= 0; j--) {
        if (attrs[j].name.startsWith('on')) {
          all[i].removeAttribute(attrs[j].name);
        }
      }
    }
  }
${restore}
})();
</script>`
}
